import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { z } from 'zod';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    type AggregateFailureEvidence,
    AggregateFailureEvidenceCard,
    ExpectedBehaviorCard,
    type ExpectSpec,
    GradingSpecCard,
    type InputTurn,
    JudgeAggregateBadges,
    type JudgeAggregateSummary,
    ModelInputCard,
    RepetitionCard,
    type RepetitionDetail,
    SourceCallCard,
    stringField,
} from '@apps/web/routes/_app/admin/voice-evals/case_report';
import { resolveCaseCostUsd } from '@apps/web/routes/_app/admin/voice-evals/matrix_summary';
import {
    CaseStatusBadge,
    evidenceFamilyForCase,
    formatDateTime,
    formatPercent,
    formatUsd,
    GitProvenanceCell,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import { useVoiceEvalCaseComparison } from '@apps/web/swr_data_fetching/admin_voice_eval_queries';
import {
    type EvalCaseResultDetailRead,
    type EvalRunListItem,
} from '@libraries/typescript/api_client';

const compareCaseSearchSchema = z.object({
    base: z.string().catch(''),
    candidate: z.string().catch(''),
    registry_id: z.string().catch(''),
});

export const Route = createFileRoute('/_app/admin/voice-evals/compare-case')({
    validateSearch: compareCaseSearchSchema,
    head: () => ({
        meta: [{ title: 'Eval Case Diff | Phoebe Admin' }],
    }),
    component: VoiceEvalCaseComparisonPage,
});

function SideRunSummary({
    label,
    run,
}: {
    label: string;
    run: EvalRunListItem;
}) {
    return (
        <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="secondary">{label}</Badge>
            <GitProvenanceCell run={run} />
            <span className="text-muted-foreground">
                {run.provider ?? '—'} / {run.model ?? '—'} ·{' '}
                {formatDateTime(run.finished_at ?? run.created_at)}
            </span>
        </div>
    );
}

/**
 * One run's column in the case diff: outcome, judge aggregates, and the
 * full per-repetition report (transcript, trace, judges).
 */
function CaseComparisonSide({
    label,
    side,
}: {
    label: string;
    side: EvalCaseResultDetailRead | null | undefined;
}) {
    if (!side) {
        return (
            <div className="space-y-3">
                <Badge variant="secondary">{label}</Badge>
                <div className="text-muted-foreground rounded-md border border-dashed p-8 text-center text-sm">
                    This case did not run in this run.
                </div>
            </div>
        );
    }
    const detail = side.detail;
    const repetitions = (detail['runs'] ?? []) as Array<RepetitionDetail>;
    const failureEvidence = (detail['failure_evidence'] ??
        []) as Array<AggregateFailureEvidence>;
    const expect = (detail['expect'] ?? null) as ExpectSpec | null;
    const binaryJudgeSummary = detail[
        'binary_llm_judge'
    ] as JudgeAggregateSummary | null;
    return (
        <div className="min-w-0 space-y-3">
            <SideRunSummary label={label} run={side.run} />
            <div className="flex flex-wrap items-center gap-2">
                <CaseStatusBadge caseResult={side.case} />
                <span className="text-muted-foreground text-xs">
                    pass rate {formatPercent(side.case.pass_rate)}
                    {side.case.score !== null &&
                        side.case.score !== undefined &&
                        ` · score ${side.case.score.toFixed(2)}`}
                    {' · known model estimate '}
                    {formatUsd(resolveCaseCostUsd(side.case))}
                    {detail['cost_tracking_complete'] === false &&
                        ' (partial; usage missing)'}
                </span>
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link
                        to="/admin/voice-evals/runs/$runId/cases/$caseResultId"
                        params={{
                            runId: side.run.id,
                            caseResultId: side.case.id,
                        }}
                    >
                        Full detail
                    </Link>
                </Button>
            </div>
            {binaryJudgeSummary && (
                <JudgeAggregateBadges
                    label="Binary judge"
                    summary={binaryJudgeSummary}
                />
            )}
            <AggregateFailureEvidenceCard evidence={failureEvidence} />
            {repetitions.length === 0 && (
                <div className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
                    No per-repetition detail was recorded.
                </div>
            )}
            {repetitions.map((repetition, index) => (
                <RepetitionCard
                    key={repetition.run_index ?? index}
                    repetition={repetition}
                    index={index}
                    family={evidenceFamilyForCase(
                        side.run.family,
                        side.case.kind,
                    )}
                    expect={expect}
                />
            ))}
        </div>
    );
}

function VoiceEvalCaseComparisonPage() {
    const search = Route.useSearch();
    const { data, isLoading, error } = useVoiceEvalCaseComparison(
        search.base || null,
        search.candidate || null,
        search.registry_id || null,
    );

    if (!search.base || !search.candidate || !search.registry_id) {
        return (
            <div className="text-muted-foreground p-8 text-center text-sm">
                Pick two runs and a case from the compare page to see a diff.
            </div>
        );
    }
    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner size="xl" tone="muted" />
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="text-muted-foreground p-8 text-center text-sm">
                Eval case comparison not found.
            </div>
        );
    }

    const reference = data.candidate ?? data.base;
    const caseMetadata = (reference?.detail['case'] ?? {}) as Record<
        string,
        unknown
    >;
    const registryMetadata = (reference?.detail['registry'] ?? null) as Record<
        string,
        unknown
    > | null;
    const sourceCallId =
        stringField(caseMetadata, 'production_source_id') ??
        stringField(registryMetadata, 'production_source_id');
    return (
        <div className="flex h-full flex-col gap-4 overflow-auto p-4">
            <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link
                        to="/admin/voice-evals/compare"
                        search={{
                            base: search.base,
                            candidate: search.candidate,
                        }}
                    >
                        <ArrowLeft className="mr-1 h-4 w-4" />
                        Compare runs
                    </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link
                        to="/admin/voice-evals/cases"
                        search={{ registry_id: data.registry_id }}
                    >
                        Case history
                    </Link>
                </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-mono text-lg font-semibold">
                    {reference?.case.case_name ?? data.registry_id}
                </h1>
                {reference?.case.use_case && (
                    <Badge variant="outline">{reference.case.use_case}</Badge>
                )}
                {reference?.case.harness && (
                    <Badge variant="secondary">{reference.case.harness}</Badge>
                )}
                <span className="text-muted-foreground text-xs">
                    {data.registry_id}
                </span>
            </div>
            {sourceCallId && <SourceCallCard sourceCallId={sourceCallId} />}
            <ModelInputCard
                inputTurns={
                    (reference?.detail['input_turns'] ??
                        null) as ReadonlyArray<InputTurn> | null
                }
            />
            <GradingSpecCard
                expect={
                    (reference?.detail['expect'] ?? null) as ExpectSpec | null
                }
            />
            <ExpectedBehaviorCard caseMetadata={caseMetadata} />
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <CaseComparisonSide label="Baseline" side={data.base} />
                <CaseComparisonSide label="Candidate" side={data.candidate} />
            </div>
        </div>
    );
}
